/**
 * Column-presets domain — cache invalidation registry.
 *
 * Defines which queries every wrapped column-preset mutation should
 * invalidate on success. Consumed by `hooks.ts` via
 * `useInvalidatingMutation`. The compiler enforces that every entry in
 * `ColumnPresetsMutationId` has a registry entry, so adding a new
 * mutation without declaring its invalidations is a build error.
 */

import type { QueryKey } from '@tanstack/react-query'

import type { InvalidationRule } from '../invalidation/useInvalidatingMutation'
import { listColumnPresetsQueryKey } from '../generated/hooks/columnPresetsController'

// ── Mutation identifiers ────────────────────────────────────────────

export type ColumnPresetsMutationId =
  | 'createColumnPreset'
  | 'updateColumnPreset'
  | 'deleteColumnPreset'

// ── Shared invalidation set ─────────────────────────────────────────

/**
 * Query keys every column-preset mutation should invalidate.
 *
 * Both `useGetAllColumnPresets` and `useGetColumnPresetOptions` are
 * thin wrappers over the same generated `useListColumnPresets`, so a
 * single invalidation of `listColumnPresetsQueryKey()` covers both
 * read paths — the `useGetColumnPresetOptions` `select` mapping
 * re-runs against the freshly fetched data automatically.
 */
export function commonColumnPresetInvalidations(): QueryKey[] {
  return [listColumnPresetsQueryKey()]
}

// ── Registry ────────────────────────────────────────────────────────

export const invalidationRegistry: Record<
  ColumnPresetsMutationId,
  InvalidationRule<any>
> = {
  createColumnPreset: {
    invalidates: () => commonColumnPresetInvalidations(),
  },
  updateColumnPreset: {
    invalidates: () => commonColumnPresetInvalidations(),
  },
  deleteColumnPreset: {
    invalidates: () => commonColumnPresetInvalidations(),
  },
}
